import { Dispatch, memo, SetStateAction, useState } from 'react';

import {
  JacksonSSOOptionstype,
  SSOAttributeRule,
  SSOAttributeRuleCondition,
  SSOAttributeRuleType,
} from '@/types/registration.types';
import { isValidAttributeRule } from '@/utils/authentication/sso-utils';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Chip from '@styles/design-system/components/chips/chip';
import Switch from '@ui/switch/switch';
import Popover from '@ui/popover/popover';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import { plural } from '@/utils/plural';
import { ICONS } from '@ui/icons/types';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';

type RenderRuleProps = {
  rule: SSOAttributeRule;
  selectedCustomSso: JacksonSSOOptionstype;
  handleToggleRule: (uuid: string, on: boolean) => void;
  handleEditRule: (uuid: string) => void;
  setSelectedRuleToDelete: Dispatch<SetStateAction<SSOAttributeRule | null>>;
  // Blocks enabling/disabling the rule (audience list gate active — rules are mutually
  // exclusive with it). Edit/Delete stay available so admins can clean rules up.
  toggleDisabled?: boolean;
};

const OptionWrapper = ({
  label,
  icon,
  rule,
  onSelect,
}: {
  label: string;
  icon: string;
  rule: SSOAttributeRule;
  onSelect: (rule: SSOAttributeRule) => void;
}) => {
  return (
    <Button hoverBackgroundColor="var(--global-cont-2)" clear onClick={() => onSelect(rule)} height="100%" width="100%">
      <Container
        design="GlobalCont1"
        inheritButtonHover
        className="flex ai-center gap-8 padding-h-16 padding-v-8"
        height="100%"
        width="100%"
      >
        <Icon name={icon} size={16} />
        <Text className="S3" design="GlobalIcoText1" whiteSpace="nowrap">
          {label}
        </Text>
      </Container>
    </Button>
  );
};

const RenderRuleCondition = ({
  ruleType,
  condition,
  isLast,
}: {
  ruleType: SSOAttributeRuleType;
  condition: SSOAttributeRuleCondition;
  isLast: boolean;
}) => {
  return (
    <Text className="text-center P2" design="GlobalIcoText1">
      <div>
        <div>{ruleType} ACCESS WHEN</div>
        <div>
          {condition.attribute} {condition.operator} {condition.value}
        </div>
      </div>
      {isLast ? null : <div>AND</div>}
    </Text>
  );
};

const RuleConditions = ({ rule }: { rule: SSOAttributeRule }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div className="flex gap-8 ai-center">
      <Chip label={rule.type === SSOAttributeRuleType.DENY ? 'Deny' : 'Allow'} size={32} type="info" />
      <TooltipPortal
        verticalOffset={-3}
        onClickAway={(meta) => {
          if (meta.clickedTriggerElement) {
            return;
          }
          setShowTooltip(false);
        }}
        onTriggerClick={() => {
          setShowTooltip((prev) => !prev);
        }}
        onEscape={() => setShowTooltip(false)}
        onTriggerTabAway={() => {
          setShowTooltip(false);
        }}
        open={showTooltip}
        styles={{ backgroundColor: 'var(--global-cont-3)', padding: '8px', borderRadius: '4px' }}
        tooltipContent={
          <Container maxWidth="168px" width="fit-content">
            {rule.conditions.map((condition, idx, arr) => (
              <RenderRuleCondition
                key={condition.value}
                ruleType={rule.type}
                condition={condition}
                isLast={idx === arr.length - 1}
              />
            ))}
          </Container>
        }
      >
        <Chip
          /* onClick handler require to let Chip know this is a "clickable" chip so we get proper styles */
          onClick={() => null}
          selected={showTooltip}
          selectedState="hover"
          selectProps={{
            onKeyDown: (e) => (e.key === 'Enter' ? setShowTooltip((prev) => !prev) : null),
            className: 'no-select',
          }}
          label={`${rule.conditions.length} ${plural(rule.conditions.length, 'condition')}`}
          size={32}
          type="info"
        />
      </TooltipPortal>
    </div>
  );
};

const RenderRule = memo(
  ({
    rule,
    selectedCustomSso,
    handleEditRule,
    handleToggleRule,
    setSelectedRuleToDelete,
    toggleDisabled,
  }: RenderRuleProps) => {
    const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

    const isValid = selectedCustomSso && isValidAttributeRule(rule, selectedCustomSso);
    return (
      <Container
        design="GlobalCont2"
        borderRadius="12px"
        padding="16px"
        border={isValid ? 'none' : '1px solid var(--global-error-500)'}
        key={rule.uuid}
        className="flex ai-center jc-space-between"
      >
        <div className="flex gap-16 ai-center">
          <Text tag="h6" className="H6" design="GlobalIcoText1" clamp={1}>
            {rule.name}
          </Text>

          <RuleConditions rule={rule} />
        </div>
        <div className="flex gap-8 ai-center">
          <Switch
            disabled={savingWorkingProject || toggleDisabled}
            value={rule.uuid}
            on={rule.enabled}
            onClick={handleToggleRule}
          />
          <Popover
            width="fit-content"
            cycleWithArrowKeys
            disabled={savingWorkingProject}
            renderTriggerButton={({ open, closing }) => (
              <Button
                design="LocalBttnSecTransp"
                fixedState={open && !closing ? 'hover' : undefined}
                designSize="small"
                round
              >
                <Icon name={ICONS.THREE_DOTS_HORIZONTAL} size={12} />
              </Button>
            )}
          >
            <Container className="flex-column" borderRadius="12px">
              <OptionWrapper
                label="Edit"
                onSelect={(selectedRule) => handleEditRule(selectedRule.uuid)}
                rule={rule}
                icon={ICONS.EDIT}
              />

              <OptionWrapper
                label="Delete"
                onSelect={(selectedRule) => setSelectedRuleToDelete(selectedRule)}
                rule={rule}
                icon={ICONS.TRASH}
              />
            </Container>
          </Popover>
        </div>
      </Container>
    );
  }
);

RenderRule.displayName = 'RenderRule';

export default RenderRule;
